import styled from "styled-components";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { CustomPopover } from "../common";

interface AnnotationPanelProps {
  annotations: any[];
  annotationInfo: {
    setSelectedAnnotation: (annotation: any) => void;
    setShowEditModal: (open: boolean) => void;
    handleDeleteAnnotation: () => void;
    getShapeType: (annotation: any) => string;
    annotationForm?: { setFieldsValue: (values: any) => void };
  };
  onClose: () => void;
  isReadOnly?: boolean;
}

export const AnnotationPanel = ({ annotations, annotationInfo, onClose, isReadOnly = false }: AnnotationPanelProps) => {
  return (
    <StPanel style={{ bottom: "4.5rem", left: "1rem", right: "auto", top: "auto" }} onClick={(e) => e.stopPropagation()}>
      <StPanelHeader>
        <span>Annotation</span>
        <StPanelClose onClick={onClose}>×</StPanelClose>
      </StPanelHeader>
      <StPanelBody>
        {annotations.length === 0 ? (
          <StPanelEmpty>어노테이션이 없습니다.</StPanelEmpty>
        ) : (
          annotations.map((annotation: any, idx: number) => (
            <StPanelItem key={`${idx}_${annotation.annoId}`}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
                <StColorDot $color={annotation.annoInfo?.[1]?.stroke || '#285F3D'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CustomPopover title={annotation.content}>
                    <StPanelItemTitle>{annotation.content}</StPanelItemTitle>
                  </CustomPopover>
                  {annotation.writer && (
                    <StPanelItemWriter>
                      {annotation.writer}
                      {annotation.anonymous === true && (
                        <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
                      )}
                    </StPanelItemWriter>
                  )}
                </div>
                <StPanelItemBadge>{annotationInfo.getShapeType(annotation)}</StPanelItemBadge>
              </div>
              {!isReadOnly && (
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <EditOutlined
                    onClick={() => {
                      annotationInfo.setSelectedAnnotation(annotation);
                      annotationInfo.annotationForm?.setFieldsValue({
                        content: annotation.content,
                        anonymousPassword: "",
                      });
                      setTimeout(() => { annotationInfo.setShowEditModal(true); }, 0);
                    }}
                    style={{ cursor: "pointer", color: "#aaa", fontSize: "0.9rem" }}
                  />
                  <DeleteOutlined
                    onClick={() => {
                      annotationInfo.setSelectedAnnotation(annotation);
                      annotationInfo.handleDeleteAnnotation();
                    }}
                    style={{ cursor: "pointer", color: "#aaa", fontSize: "0.9rem" }}
                  />
                </div>
              )}
            </StPanelItem>
          ))
        )}
      </StPanelBody>
    </StPanel>
  );
};

// --- Styled Components ---

const StPanel = styled.div`
  position: absolute;
  bottom: 40px;
  right: 10px;
  width: 260px;
  max-height: 320px;
  background-color: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: rgba(8, 9, 9, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const StPanelClose = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;

  &:hover {
    color: white;
  }
`;

const StPanelBody = styled.div`
  padding: 6px 8px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const StPanelEmpty = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
  text-align: center;
  padding: 12px 0;
`;

const StPanelItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: white;

  &:last-child {
    border-bottom: none;
  }
`;

interface StColorDotProps {
  $color: string;
}

const StColorDot = styled.div<StColorDotProps>`
  width: 12px;
  height: 12px;
  background-color: ${props => props.$color};
  border-radius: 3px;
  flex-shrink: 0;
`;

const StPanelItemTitle = styled.div`
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const StPanelItemWriter = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
`;

const StTempUserLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
`;

const StPanelItemBadge = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;
`;
